import type { Licence, Locale } from '@seek/ca-myprofile-utils';
import { metrics } from '@seek/metrics-js';
import { LicenceForm, VerifiedLicenceForm } from '@seek/profile-edit-ui';
import type React from 'react';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { log } from 'src/modules/error-logger';
import type { AllOrNone, Maybe, ActionName } from 'src/types';

import type { ProfilePageSettings } from '../../CreateResume/reducer';
import type { LicenceUpdate } from '../types';

type CertsyDialogProps = AllOrNone<{
  manageVerificationUrl: Maybe<string>;
  verificationMessage: string;
}>;
interface LicenceFormRendererProps {
  activeLicence: Licence | undefined;
  dataLoadedFromUrl: boolean;
  isVerifiedLicence: boolean;
  locale: Locale;
  actionName: ActionName;
  onFormDirtyChange: (dirty: boolean) => void;
  submitForm: (params: { dirty: boolean; values: LicenceUpdate }) => void;
  submitVerifiedForm: (params: {
    dirty: boolean;
    values: { description: string };
  }) => void;
  onCancel: () => void;
  urlParams: ProfilePageSettings;
  validateOnMount: boolean;
  setDataLoadedFromUrl: React.Dispatch<React.SetStateAction<boolean>>;
  onManageClick: () => void;
  onCertsyClick: () => void;
  onDelete?: () => void;
}

export const LicenceFormRenderer = ({
  activeLicence,
  dataLoadedFromUrl,
  isVerifiedLicence,
  locale,
  actionName,
  onFormDirtyChange,
  submitForm,
  submitVerifiedForm,
  onCancel,
  urlParams,
  validateOnMount,
  setDataLoadedFromUrl,
  onManageClick,
  onCertsyClick,
  onDelete,
}: LicenceFormRendererProps) => {
  let initialValues: Licence | undefined;
  const page = usePage();

  if (urlParams?.formData) {
    const { formData, profileDataSource } = urlParams;
    try {
      initialValues = JSON.parse(decodeURIComponent(formData));
      if (dataLoadedFromUrl === false) {
        setDataLoadedFromUrl(true);
        metrics.count('data-url', [`data-source:${profileDataSource || ''}`]);
      }
    } catch {
      log(new Error('Licence data could not be parsed'), {
        name: 'Licence Form',
      });
    }
  }

  const formLicence = activeLicence ?? initialValues;

  const onSubmitFailFn = (errors: string[]) => {
    onSubmitFail(errors, page);
  };

  const certsyDialogProps = (): CertsyDialogProps => {
    if (
      activeLicence?.credential?.manageVerificationUrl &&
      activeLicence.verificationMessage
    ) {
      const {
        credential: { manageVerificationUrl },
        verificationMessage,
      } = activeLicence;

      return {
        manageVerificationUrl,
        verificationMessage,
      };
    }

    return {};
  };

  const licenceForm = (
    <LicenceForm
      locale={locale}
      initialValues={formLicence}
      onSubmit={submitForm}
      onCancel={onCancel}
      validateOnMount={validateOnMount}
      scrollToTopOnMobile={false}
      onFormDirtyChange={onFormDirtyChange}
      onSubmitFail={onSubmitFailFn}
      {...(onDelete && { onDelete })}
    />
  );

  const verifiedLicenceForm = (
    <VerifiedLicenceForm
      onSubmit={submitVerifiedForm}
      onCancel={onCancel}
      initialValues={formLicence!}
      name={formLicence?.name.text ?? ''}
      issuingOrganisation={formLicence?.issuingOrganisation ?? ''}
      credentialInfo={formLicence?.credential?.credentialInfo ?? []}
      formattedDate={formLicence?.formattedDate ?? ''}
      status={activeLicence?.status ?? null}
      onManageClick={onManageClick}
      onCertsyClick={onCertsyClick}
      onFormDirtyChange={onFormDirtyChange}
      {...certsyDialogProps()}
      {...(onDelete && { onDelete })}
    />
  );

  return actionName === EDIT && isVerifiedLicence
    ? verifiedLicenceForm
    : licenceForm;
};
